import { useMemo, useSyncExternalStore } from "react";

export type WaveSyncLogEntry = {
  id: string;
  category: string;
  message: string;
  detail?: string | null;
  at: number;
};

const STORAGE_CAPTURE_KEY = "wave.yjs.syncLog";
const MAX_ENTRIES = 120;

let capturing = false;
let revision = 0;
const entries: WaveSyncLogEntry[] = [];
const listeners = new Set<() => void>();

function loadCapturePref(): boolean {
  try {
    return localStorage.getItem(STORAGE_CAPTURE_KEY) === "1";
  } catch {
    return false;
  }
}

capturing = loadCapturePref();

function notify(): void {
  revision += 1;
  for (const listener of listeners) listener();
}

export function getWaveSyncLogRevision(): number {
  return revision;
}

export function subscribeWaveSyncLog(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getWaveSyncLogEntries(): WaveSyncLogEntry[] {
  return [...entries];
}

export function isWaveSyncLogCapturing(): boolean {
  return capturing;
}

export function setWaveSyncLogCapturing(enabled: boolean): void {
  capturing = enabled;
  try {
    localStorage.setItem(STORAGE_CAPTURE_KEY, enabled ? "1" : "0");
  } catch {
    /* ignore */
  }
  notify();
}

export function appendWaveSyncLog(
  category: string,
  message: string,
  detail?: string | null,
): void {
  if (!capturing) return;
  entries.push({
    id: crypto.randomUUID(),
    category,
    message,
    detail: detail ?? null,
    at: Date.now(),
  });
  if (entries.length > MAX_ENTRIES) {
    entries.splice(0, entries.length - MAX_ENTRIES);
  }
  notify();
}

export function exportWaveSyncLogText(): string {
  return entries
    .map((entry) => {
      const time = new Date(entry.at).toISOString();
      const detail = entry.detail?.trim() ? `\n  ${entry.detail.trim()}` : "";
      return `[${time}] ${entry.category}: ${entry.message}${detail}`;
    })
    .join("\n");
}

export function useWaveSyncLog(): {
  capturing: boolean;
  entries: WaveSyncLogEntry[];
  setCapturing: (enabled: boolean) => void;
  copyLog: () => Promise<void>;
} {
  const logRevision = useSyncExternalStore(
    subscribeWaveSyncLog,
    getWaveSyncLogRevision,
    () => 0,
  );
  const capturing = useSyncExternalStore(
    subscribeWaveSyncLog,
    isWaveSyncLogCapturing,
    () => false,
  );
  const entries = useMemo(() => getWaveSyncLogEntries(), [logRevision]);
  return {
    capturing,
    entries,
    setCapturing: setWaveSyncLogCapturing,
    copyLog: async () => {
      const text = exportWaveSyncLogText();
      if (!text) return;
      await navigator.clipboard.writeText(text);
    },
  };
}
